import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Raccoon — a masked little bandit standing up on its hind legs, storybook
// style: a round grey body, a big head with the classic dark eye mask, pointy
// ears, little paws held up in front of the chest and a ringed striped tail
// curving up behind. Faces +Z, stands on y = 0, about 0.6 m tall.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"  : hips + chest + belly. Breathes (squash/stretch about the
//               ground contact) so the raccoon never looks dead.
//   - "head"  : the whole head assembly (mask, ears, muzzle, eyes). Pans and
//               tilts about the neck — the curious look-around lives here.
//   - "arm-l" / "arm-r" : the little raised paws. Pivot at each shoulder so
//               they can pat and fidget.
//   - "tail"  : the ringed tail. Sways about its root at the base of the back.
// The stubby planted legs and feet are static (unnamed).

const COAT = "#8f8a99";        // soft violet-grey coat
const COAT_DARK = "#6e687c";   // shaded haunches / ear backs / arms
const MASK = "#4a4152";        // the dark bandit mask + dark tail rings
const CREAM = "#f6efe3";       // belly, muzzle, inner ears
const RING_LIGHT = "#d9cfc0";  // the pale tail rings
const NOSE = "#463a30";        // warm dark nose (never black)
const EYE_WHITE = "#f7f3e8";   // eye whites (never pure white)
const PUPIL = "#33261c";       // warm dark pupils

// The pivots the animations move the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];            // ground contact
const HEAD_PIVOT: [number, number, number] = [0, 0.38, 0.02];      // the neck
const SHOULDER_L: [number, number, number] = [-0.10, 0.335, 0.04]; // arm hinges
const SHOULDER_R: [number, number, number] = [0.10, 0.335, 0.04];
const TAIL_PIVOT: [number, number, number] = [0, 0.14, -0.13];     // tail root

// One big friendly eye: white ball, dark pupil slightly forward, tiny
// highlight. Sits proud of the dark mask so it reads bright, never scary.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.036, 10, 8), color: EYE_WHITE,
      position: [x, 0.495, 0.14], name: "head" },
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: PUPIL,
      position: [x, 0.495, 0.168], name: "head" },
    { geometry: new THREE.SphereGeometry(0.007, 6, 5), color: CREAM,
      position: [x + 0.008, 0.505, 0.181], name: "head" },
  ];
}

// One pointy ear: a dark cone with a cream inner cone, tilted outward.
function ear(side: 1 | -1): Part[] {
  const x = side * 0.09;
  return [
    { geometry: new THREE.ConeGeometry(0.045, 0.09, 5), color: COAT_DARK,
      position: [x, 0.61, 0.01], rotation: [0, 0, -side * 0.26], name: "head" },
    { geometry: new THREE.ConeGeometry(0.025, 0.055, 5), color: CREAM,
      position: [x, 0.60, 0.026], rotation: [0, 0, -side * 0.26], name: "head" },
  ];
}

// One raised little paw: a stubby forearm angled up in front of the chest
// with a cream paw ball on the end — the classic "up to no good" pose.
function paw(side: 1 | -1): Part[] {
  const name = side < 0 ? "arm-l" : "arm-r";
  const x = side * 0.10;
  return [
    // Shoulder ball tucked against the chest.
    { geometry: new THREE.SphereGeometry(0.038, 8, 6), color: COAT_DARK,
      position: [x, 0.335, 0.04], name },
    // Forearm reaching forward and up.
    { geometry: new THREE.CylinderGeometry(0.026, 0.03, 0.10, 8), color: COAT_DARK,
      position: [x - side * 0.012, 0.325, 0.095], rotation: [-1.25, 0, side * 0.15], name },
    // The little cream paw held up.
    { geometry: new THREE.SphereGeometry(0.034, 8, 6), color: CREAM,
      position: [x - side * 0.022, 0.33, 0.14], name },
  ];
}

// The ringed tail: alternating dark/pale ring segments stacked along an axis
// tilted up-and-back from the tail root, capped with a dark tip.
function tail(): Part[] {
  const tilt = -0.85;                        // lean the segment axis up-and-back
  const dirY = Math.cos(tilt), dirZ = Math.sin(tilt);
  const root: [number, number, number] = [0, 0.14, -0.13];
  const parts: Part[] = [];
  const radii = [0.052, 0.048, 0.043, 0.037, 0.030];
  for (let i = 0; i < radii.length; i++) {
    const d = 0.028 + i * 0.052;
    parts.push({
      geometry: new THREE.CylinderGeometry(radii[i], radii[i], 0.055, 10),
      color: i % 2 === 0 ? MASK : RING_LIGHT,
      position: [root[0], root[1] + dirY * d, root[2] + dirZ * d],
      rotation: [tilt, 0, 0], name: "tail",
    });
  }
  // Dark rounded tip.
  const dTip = 0.028 + radii.length * 0.052;
  parts.push({
    geometry: new THREE.SphereGeometry(0.03, 8, 6), color: MASK,
    position: [root[0], root[1] + dirY * dTip, root[2] + dirZ * dTip],
    name: "tail",
  });
  return parts;
}

export const raccoon = defineModel({
  id: "raccoon",
  batchId: "mess",
  name: "Raccoon",
  tags: ["mess", "cute", "animated", "creature", "animal", "raccoon", "mammal", "forest"],
  summary: "A masked raccoon standing on its hind legs with little paws held up.",
  description:
    "A storybook raccoon caught mid-mischief: standing upright on stubby hind " +
    "legs, a round grey body with a cream belly, a big head wearing the classic " +
    "dark bandit mask over big friendly eyes, pointy ears, little paws held up " +
    "in front of the chest and a ringed striped tail curving up behind. " +
    "Breathes at rest (idle) and does a shifty, curious look-around " +
    "(look-around).",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body (named, breathes) ──────────────────────────────────────────
      // Hips: the wide round base of the standing pear.
      { geometry: new THREE.SphereGeometry(0.135, 12, 10), color: COAT,
        position: [0, 0.185, -0.005], scale: [1.1, 1.0, 1.0], name: "body" },
      // Chest rising toward the head.
      { geometry: new THREE.SphereGeometry(0.105, 12, 10), color: COAT,
        position: [0, 0.315, 0.01], name: "body" },
      // Cream belly patch on the front.
      { geometry: new THREE.SphereGeometry(0.085, 10, 8), color: CREAM,
        position: [0, 0.24, 0.075], scale: [0.9, 1.1, 0.6], name: "body" },
      // A darker saddle over the back for shading.
      { geometry: new THREE.SphereGeometry(0.11, 10, 8), color: COAT_DARK,
        position: [0, 0.24, -0.08], scale: [1.0, 0.95, 0.75], name: "body" },

      // ── hind legs + feet (static, planted) ──────────────────────────────
      { geometry: new THREE.CylinderGeometry(0.038, 0.042, 0.12, 8), color: COAT_DARK,
        position: [-0.065, 0.06, 0.01] },
      { geometry: new THREE.CylinderGeometry(0.038, 0.042, 0.12, 8), color: COAT_DARK,
        position: [0.065, 0.06, 0.01] },
      // Big flat feet reaching forward.
      { geometry: new THREE.SphereGeometry(0.048, 8, 6), color: MASK,
        position: [-0.065, 0.032, 0.045], scale: [1.0, 0.65, 1.5] },
      { geometry: new THREE.SphereGeometry(0.048, 8, 6), color: MASK,
        position: [0.065, 0.032, 0.045], scale: [1.0, 0.65, 1.5] },

      // ── head (named, looks around) ───────────────────────────────────────
      { geometry: new THREE.SphereGeometry(0.135, 14, 12), color: COAT,
        position: [0, 0.475, 0.02], name: "head" },
      // The dark bandit mask: a wide flattened band across the eyes.
      { geometry: new THREE.SphereGeometry(0.07, 10, 8), color: MASK,
        position: [0, 0.497, 0.112], scale: [1.95, 0.62, 0.6], name: "head" },
      // Cream muzzle under the mask.
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: CREAM,
        position: [0, 0.428, 0.135], scale: [1.2, 0.8, 0.8], name: "head" },
      // The little dark nose on the tip.
      { geometry: new THREE.SphereGeometry(0.022, 8, 6), color: NOSE,
        position: [0, 0.443, 0.188], name: "head" },
      ...ear(1), ...ear(-1),
      ...eye(0.055), ...eye(-0.055),

      // ── raised paws (named, fidget) ──────────────────────────────────────
      ...paw(-1), ...paw(1),

      // ── ringed tail (named, sways) ───────────────────────────────────────
      ...tail(),
    ];
  },
  animations: [
    // Idle — a slow breath: the body squashes/stretches about the ground, the
    // head rides it with a gentle bob, the paws and tail sway softly.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.5, value: [1.015, 1.035, 1.015], ease: "sine" },
            { t: 3.0, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: HEAD_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.012, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.02, 0, 0.012], ease: "sine" },
            { t: 1.5, value: [-0.02, 0, -0.012], ease: "sine" },
            { t: 3.0, value: [0.02, 0, 0.012], ease: "sine" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.10, 0, 0.05], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [-0.10, 0, -0.05], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "sine" },
            { t: 3.0, value: [-0.10, 0, -0.05], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0.04, 0.06, 0], ease: "sine" },
            { t: 1.5, value: [-0.04, -0.06, 0], ease: "sine" },
            { t: 3.0, value: [0.04, 0.06, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Look-around — the signature move: a shifty, curious scan. The head pans
    // right, then left, with a nosey tilt at each end; the paws fidget
    // alternately (patting the air) and the tail counter-sways.
    {
      id: "look-around",
      label: "Curious look-around",
      duration: 4.0,
      loop: true,
      tracks: [
        {
          target: "head", pivot: HEAD_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.0, value: [0.06, -0.55, -0.08], ease: "easeInOut" },
            { t: 1.6, value: [0.06, -0.55, 0.08], ease: "easeInOut" },
            { t: 2.4, value: [0.06, 0.55, 0.08], ease: "easeInOut" },
            { t: 3.0, value: [0.06, 0.55, -0.08], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "body", pivot: BODY_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.3, value: [0, -0.12, 0], ease: "easeInOut" },
            { t: 2.7, value: [0, 0.12, 0], ease: "easeInOut" },
            { t: 4.0, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "arm-l", pivot: SHOULDER_L,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.35, 0, 0.1], ease: "sine" },
            { t: 1.0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [-0.35, 0, 0.1], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
            { t: 4.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "arm-r", pivot: SHOULDER_R,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
            { t: 2.5, value: [-0.35, 0, -0.1], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
            { t: 3.5, value: [-0.35, 0, -0.1], ease: "sine" },
            { t: 4.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.3, value: [0, 0.25, 0.1], ease: "sine" },
            { t: 2.7, value: [0, -0.25, -0.1], ease: "sine" },
            { t: 4.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
